<template>
  <a-modal
    :visible="visible"
    :width="800"
    :title="title"
    @ok="handleOk"
    @cancel="handleCancel"
    :confirmLoading="loading"
  >
    <!-- 搜索栏 -->
    <div class="search-bar">
      <a-input
        v-model="searchName"
        placeholder="请输入姓名搜索"
        class="search-input"
        allowClear
        @pressEnter="handleSearch"
      />
      <a-button type="primary" @click="handleSearch" class="search-btn">
        查询
      </a-button>
      <a-button @click="handleReset" class="reset-btn">重置</a-button>
    </div>

    <!-- 已选提示 -->
    <div class="selected-tip" v-if="multiple && selectedRowKeys.length > 0">
      已选择 <span class="selected-count">{{ selectedRowKeys.length }}</span> 人：
      <span class="selected-names">{{ selectedUserNames }}</span>
    </div>

    <!-- 用户列表表格 -->
    <a-table
      :columns="columns"
      :data-source="userList"
      :row-key="record => record.id"
      :pagination="false"
      :loading="loading"
      :row-selection="rowSelection"
      size="middle"
      :scroll="{ y: 400 }"
    >
      <span slot="index" slot-scope="text, record, index">
        {{ index + 1 + (page - 1) * pageSize }}
      </span>
    </a-table>

    <!-- 分页 -->
    <div class="pagination-wrapper">
      <a-pagination
        v-model="page"
        :page-size="pageSize"
        :total="total"
        :show-total="total => `共 ${total} 条`"
        show-size-changer
        :page-size-options="['10', '20', '50', '100']"
        @change="handlePageChange"
        @showSizeChange="handleSizeChange"
      />
    </div>
  </a-modal>
</template>

<script>
import { userColumns } from './columns';

export default {
  name: 'UserSelectModal',
  props: {
    // 控制弹窗显示
    visible: {
      type: Boolean,
      default: false
    },
    // 弹窗标题
    title: {
      type: String,
      default: '选择人员'
    },
    // 是否多选
    multiple: {
      type: Boolean,
      default: true
    },
    // 已选中的用户ID（支持单个ID或ID数组）
    selectedIds: {
      type: [Array, Number, String],
      default: () => []
    },
    // 获取用户列表的API方法
    fetchApi: {
      type: Function,
      required: true
    },
    // 额外的查询参数（如部门ID等）
    extraParams: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      columns: userColumns,
      userList: [], // 当前页显示的数据
      allUserList: [], // 所有用户数据
      loading: false,
      searchName: '',
      page: 1,
      pageSize: 10,
      total: 0,
      selectedRowKeys: [],
      selectedRows: []
    };
  },
  computed: {
    rowSelection() {
      return {
        type: this.multiple ? 'checkbox' : 'radio',
        selectedRowKeys: this.selectedRowKeys,
        onChange: this.onSelectChange
      };
    },
    // 获取选中用户的姓名列表
    selectedUserNames() {
      if (!this.selectedRowKeys.length || !this.allUserList.length) {
        return '';
      }
      const names = this.selectedRowKeys
        .map(id => {
          const user = this.allUserList.find(u => u.id === id);
          return user ? user.fullName : '';
        })
        .filter(name => name)
        .join('、');
      return names;
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.initSelectedData();
        this.fetchUserList();
      }
    }
  },
  methods: {
    // 初始化已选数据
    initSelectedData() {
      if (this.selectedIds) {
        if (Array.isArray(this.selectedIds)) {
          this.selectedRowKeys = [...this.selectedIds];
        } else if (this.selectedIds) {
          this.selectedRowKeys = [this.selectedIds];
        }
      } else {
        this.selectedRowKeys = [];
      }
    },
    
    // 获取用户列表（一次性加载所有数据）
    async fetchUserList() {
      // 检查 fetchApi 是否存在
      if (!this.fetchApi || typeof this.fetchApi !== 'function') {
        console.error('fetchApi is not a function:', this.fetchApi);
        this.$message.error('获取用户列表方法未定义');
        return;
      }

      this.loading = true;
      try {
        // 一次性加载所有数据（不传分页参数或传大数）
        const params = {
          fullName: '',
          page: 1,
          size: 9999,
          ...this.extraParams
        };
        
        const res = await this.fetchApi(params);
        
        if (res.data.code === 0) {
          this.allUserList = res.data.data || [];
          // 前端搜索和分页
          this.filterAndPaginateData();
        } else {
          this.$message.error(res.data.message || '获取用户列表失败');
        }
      } catch (error) {
        console.error('获取用户列表失败:', error);
        this.$message.error('获取用户列表失败');
      } finally {
        this.loading = false;
      }
    },

    // 前端搜索和分页
    filterAndPaginateData() {
      // 1. 搜索过滤
      let filteredData = this.allUserList;
      if (this.searchName) {
        const searchLower = this.searchName.toLowerCase();
        filteredData = this.allUserList.filter(user => 
          user.fullName && user.fullName.toLowerCase().includes(searchLower)
        );
      }

      // 2. 计算总数
      this.total = filteredData.length;

      // 3. 分页切割
      const start = (this.page - 1) * this.pageSize;
      const end = start + this.pageSize;
      this.userList = filteredData.slice(start, end);
    },

    // 选择变化
    onSelectChange(selectedRowKeys, selectedRows) {
      this.selectedRowKeys = selectedRowKeys;
      this.selectedRows = selectedRows;
    },

    // 搜索
    handleSearch() {
      this.page = 1;
      this.filterAndPaginateData();
    },

    // 重置
    handleReset() {
      this.searchName = '';
      this.page = 1;
      this.filterAndPaginateData();
    },

    // 分页变化
    handlePageChange(page, pageSize) {
      this.page = page;
      this.pageSize = pageSize;
      this.filterAndPaginateData();
    },

    // 每页条数变化
    handleSizeChange(current, size) {
      this.page = 1;
      this.pageSize = size;
      this.filterAndPaginateData();
    },

    // 确定
    handleOk() {
      if (this.selectedRowKeys.length === 0) {
        this.$message.warning('请至少选择一个人员');
        return;
      }

      // 从所有用户数据中获取完整的用户信息
      const selectedUsers = this.allUserList.filter(user => 
        this.selectedRowKeys.includes(user.id)
      );

      const result = {
        ids: this.multiple ? this.selectedRowKeys : this.selectedRowKeys[0],
        users: selectedUsers,
        names: selectedUsers.map(u => u.fullName).join('、')
      };

      this.$emit('confirm', result);
      this.handleCancel();
    },

    // 取消
    handleCancel() {
      this.searchName = '';
      this.page = 1;
      this.selectedRowKeys = [];
      this.selectedRows = [];
      this.$emit('update:visible', false);
      this.$emit('cancel');
    }
  }
};
</script>

<style lang="scss" scoped>
.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 16px;

  .search-input {
    width: 200px;
    margin-right: 10px;
  }

  .search-btn {
    margin-right: 10px;
  }
}

.selected-tip {
  margin-bottom: 12px;
  padding: 8px 12px;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 4px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);

  .selected-count {
    color: #1890ff;
    font-weight: bold;
    margin: 0 4px;
  }

  .selected-names {
    color: #1890ff;
    margin-left: 8px;
  }
}

.pagination-wrapper {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
</style>



// WEBPACK FOOTER //
// src/components/user-select-modal/index.vue